import { useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IdCard, Download, RotateCw, MapPin, Save, Pencil } from "lucide-react";
import { toast } from "sonner";
import { STATUS_CLS, fmtMins } from "@/components/attendanceUi";
import HrTabs from "./HrTabs";

const errText = (e) => formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong";
const tm = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "");
const iso = (d) => d.toISOString().slice(0, 10);
const sel = "h-9 border border-slate-200 rounded-md px-2 text-sm bg-white";
const TYPES = [["office", "Office"], ["field", "Field"], ["wfh", "Work from home"], ["exempt", "Exempt"]];
const STATUSES = [["", "All statuses"], ["present", "Present"], ["working", "Clocked in"], ["absent_no_clockout", "Absent — no clock-out"]];
const map = (c) => (c?.lat != null ? `https://www.google.com/maps?q=${c.lat},${c.lng}` : null);

export const canAdminAttendance = (u) => u?.role === "admin" || (u?.module_permissions || []).includes("hr_attendance_admin");

function Loc({ c }) {
  if (!c) return null;
  const href = map(c);
  return (
    <div className="text-xs text-slate-500 max-w-[260px] truncate" title={c.location}>
      {href ? <a href={href} target="_blank" rel="noopener noreferrer" className="hover:underline"><MapPin className="w-3 h-3 inline mr-0.5" />{c.location}</a> : c.location}
      {c.distance_m != null && <span className={c.inside_geofence ? "text-green-700" : "text-slate-400"}> · {c.distance_m.toLocaleString()} m</span>}
    </div>
  );
}

/* HR → Attendance Admin (backend/hr_attendance.py): today's board, records
   (filters, Excel export, clock-out corrections), each employee's
   attendance type, and the office location / radius / hours. */
export default function AttendanceAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("today");
  if (!canAdminAttendance(user)) {
    return <div className="space-y-4"><HrTabs /><div className="text-sm text-slate-500">HR attendance admins only.</div></div>;
  }
  return (
    <div className="space-y-4" data-testid="hr-attendance-admin">
      <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}><IdCard className="w-5 h-5 text-blue-600" /> HR</h1>
      <HrTabs />
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {[["today", "Today"], ["records", "Records"], ["staff", "Employees"], ["settings", "Office & hours"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`att-admin-${k}`}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === k ? "border-blue-600 text-blue-700 font-medium" : "border-transparent text-slate-600 hover:text-slate-900"}`}>{l}</button>
        ))}
      </div>
      {tab === "today" && <Today />}
      {tab === "records" && <Records />}
      {tab === "staff" && <Staff />}
      {tab === "settings" && <SettingsForm />}
    </div>
  );
}

function Today() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/hr/attendance/today").then(({ data: d }) => setData(d)).catch((e) => toast.error(errText(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const count = (s) => data.rows.filter((r) => r.status === s).length;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap text-sm">
        {[["working", "Clocked in"], ["present", "Clocked out"], ["absent_no_clockout", "Absent — no clock-out"], ["not_in", "Not clocked in"]].map(([s, l]) => (
          <span key={s} className={`rounded-full border px-2.5 py-0.5 ${STATUS_CLS[s]}`}>{l}: <b>{count(s)}</b></span>
        ))}
        <span className="text-xs text-slate-500">Clock-out closes at {tm(data.clockout_deadline)}</span>
        <Button size="sm" variant="outline" onClick={load} className="ml-auto"><RotateCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="att-today">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><tr>
            <th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">In</th>
            <th className="text-left px-3 py-2">Out</th><th className="text-left px-3 py-2">Status</th></tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2"><div className="font-medium">{r.name}</div><div className="text-xs text-slate-500 font-mono">{r.employee_code}</div></td>
                <td className="px-3 py-2">{TYPES.find((t) => t[0] === r.attendance_type)?.[1]}</td>
                <td className="px-3 py-2"><div className="font-mono">{tm(r.record?.clock_in?.at)}</div><Loc c={r.record?.clock_in} /></td>
                <td className="px-3 py-2"><div className="font-mono">{tm(r.record?.clock_out?.at)}</div><Loc c={r.record?.clock_out} /></td>
                <td className="px-3 py-2"><span className={`text-xs rounded-full border px-2 py-0.5 whitespace-nowrap ${STATUS_CLS[r.status]}`}>{r.label}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Records() {
  const today = new Date();
  const [f, setF] = useState({ start: iso(new Date(today.getTime() - 6 * 864e5)), end: iso(today), user_id: "", attendance_type: "", status: "" });
  const [rows, setRows] = useState(null);
  const [staff, setStaff] = useState([]);
  const [fix, setFix] = useState(null);       // {row, time, reason}
  const load = useCallback(async () => {
    setRows(null);
    try { const { data } = await api.get("/hr/attendance/records", { params: f }); setRows(data.rows); } catch (e) { toast.error(errText(e)); setRows([]); }
  }, [f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/hr/attendance/staff").then(({ data }) => setStaff(data)).catch(() => {}); }, []);

  const exportXlsx = () => {
    const head = ["Date", "Employee ID", "Name", "Attendance type", "Clock in", "In latitude", "In longitude", "In accuracy (m)",
      "In distance from office (m)", "In location", "Clock out", "Out latitude", "Out longitude", "Out location", "Worked", "Status", "HR correction"];
    const body = (rows || []).map((r) => [r.date, r.employee_code, r.name, r.type_label, tm(r.clock_in?.at), r.clock_in?.lat, r.clock_in?.lng,
      r.clock_in?.accuracy != null ? Math.round(r.clock_in.accuracy) : "", r.clock_in?.distance_m, r.clock_in?.location,
      tm(r.clock_out?.at), r.clock_out?.lat ?? "", r.clock_out?.lng ?? "", r.clock_out?.location || "",
      r.status === "present" ? fmtMins(r.worked_min) : "", r.label, r.clock_out?.reason ? `${r.clock_out.corrected_by_name}: ${r.clock_out.reason}` : ""]);
    const ws = XLSX.utils.aoa_to_sheet([head, ...body]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    XLSX.writeFile(wb, `attendance-${f.start}-to-${f.end}.xlsx`);
  };

  const saveFix = async () => {
    try {
      await api.post(`/hr/attendance/records/${fix.row.user_id}/${fix.row.date}/clock-out`, { clock_out_time: fix.time, reason: fix.reason });
      toast.success("Clock-out set"); setFix(null); load();
    } catch (e) { toast.error(errText(e)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div><Label className="text-xs">From</Label><Input type="date" value={f.start} onChange={set("start")} /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={f.end} onChange={set("end")} /></div>
        <select className={sel} value={f.user_id} onChange={set("user_id")}><option value="">All employees</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        <select className={sel} value={f.attendance_type} onChange={set("attendance_type")}><option value="">All types</option>{TYPES.slice(0, 3).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select className={sel} value={f.status} onChange={set("status")}>{STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <Button size="sm" variant="outline" onClick={exportXlsx} disabled={!rows?.length} className="ml-auto" data-testid="att-export"><Download className="w-3.5 h-3.5 mr-1" />Export</Button>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="att-records">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><tr>
            <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">In</th><th className="text-left px-3 py-2">Out</th><th className="text-left px-3 py-2">Worked</th>
            <th className="text-left px-3 py-2">Status</th><th /></tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>}
            {rows?.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">No attendance in this range.</td></tr>}
            {rows?.map((r) => (
              <tr key={`${r.user_id}-${r.date}`} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2 whitespace-nowrap">{r.date}</td>
                <td className="px-3 py-2"><div className="font-medium">{r.name}</div><div className="text-xs text-slate-500 font-mono">{r.employee_code}</div></td>
                <td className="px-3 py-2">{r.type_label}</td>
                <td className="px-3 py-2"><div className="font-mono">{tm(r.clock_in?.at)}</div><Loc c={r.clock_in} /></td>
                <td className="px-3 py-2"><div className="font-mono">{tm(r.clock_out?.at)}</div><Loc c={r.clock_out} />
                  {r.clock_out?.reason && <div className="text-[11px] text-amber-700">HR: {r.clock_out.reason}</div>}</td>
                <td className="px-3 py-2">{r.status === "present" ? fmtMins(r.worked_min) : "—"}</td>
                <td className="px-3 py-2"><span className={`text-xs rounded-full border px-2 py-0.5 whitespace-nowrap ${STATUS_CLS[r.status]}`}>{r.label}</span></td>
                <td className="px-3 py-2">{r.status !== "present" && (
                  <Button size="sm" variant="outline" className="h-7" onClick={() => setFix({ row: r, time: "18:00", reason: "" })}><Pencil className="w-3.5 h-3.5 mr-1" />Set clock-out</Button>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {fix && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex flex-wrap items-end gap-2" data-testid="att-fix">
          <div className="text-sm w-full">Set the clock-out for <b>{fix.row.name}</b> on {fix.row.date} — this counts the day as present.</div>
          <div><Label className="text-xs">Clock-out time</Label><Input type="time" value={fix.time} onChange={(e) => setFix({ ...fix, time: e.target.value })} /></div>
          <div className="flex-1 min-w-[220px]"><Label className="text-xs">Reason (kept on the record)</Label><Input value={fix.reason} onChange={(e) => setFix({ ...fix, reason: e.target.value })} placeholder="e.g. Forgot to clock out; confirmed by supervisor" /></div>
          <Button size="sm" onClick={saveFix}>Save</Button>
          <Button size="sm" variant="outline" onClick={() => setFix(null)}>Cancel</Button>
        </div>
      )}
    </div>
  );
}

function Staff() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/hr/attendance/staff").then(({ data }) => setRows(data)).catch((e) => toast.error(errText(e))); }, []);
  const change = async (u, t) => {
    try { await api.put(`/hr/attendance/staff/${u.id}`, { attendance_type: t }); setRows((rs) => rs.map((r) => (r.id === u.id ? { ...r, attendance_type: t } : r))); toast.success(`${u.name}: ${TYPES.find((x) => x[0] === t)[1]}`); }
    catch (e) { toast.error(errText(e)); }
  };
  if (!rows) return <div className="text-sm text-slate-500">Loading…</div>;
  const shown = rows.filter((r) => `${r.name} ${r.employee_code} ${r.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600"><b>Office</b>: clock-in only inside the office radius. <b>Field</b>: anywhere, live GPS recorded. <b>Work from home</b>: GPS recorded. <b>Exempt</b>: no GPS attendance.</p>
      <Input placeholder="Search employees" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="att-staff">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">ID</th><th className="text-left px-3 py-2">Attendance type</th></tr></thead>
          <tbody>
            {shown.map((u) => (
              <tr key={u.id} className="border-t border-slate-100">
                <td className="px-3 py-1.5">{u.name}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-slate-500">{u.employee_code}</td>
                <td className="px-3 py-1.5"><select className={sel} value={u.attendance_type} onChange={(e) => change(u, e.target.value)} data-testid={`att-type-${u.id}`}>
                  {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsForm() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/hr/attendance/settings").then(({ data }) => setS(data)).catch((e) => toast.error(errText(e))); }, []);
  if (!s) return <div className="text-sm text-slate-500">Loading…</div>;
  const set = (k, num) => (e) => setS({ ...s, [k]: num ? Number(e.target.value) : e.target.value });
  const save = async () => {
    try { const { data } = await api.put("/hr/attendance/settings", s); setS(data); toast.success("Saved"); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="max-w-2xl rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="att-settings">
      <div><Label>Office</Label><Input value={s.office_name} onChange={set("office_name")} /></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div><Label>Latitude</Label><Input type="number" step="any" value={s.office_lat} onChange={set("office_lat", true)} /></div>
        <div><Label>Longitude</Label><Input type="number" step="any" value={s.office_lng} onChange={set("office_lng", true)} /></div>
        <div><Label>Clock-in radius (m)</Label><Input type="number" value={s.radius_m} onChange={set("radius_m", true)} /></div>
        <div><Label>Office starts</Label><Input type="time" value={s.office_start} onChange={set("office_start")} /></div>
        <div><Label>Office ends</Label><Input type="time" value={s.office_end} onChange={set("office_end")} /></div>
        <div><Label>Clock-out grace (min)</Label><Input type="number" value={s.clockout_grace_min} onChange={set("clockout_grace_min", true)} /></div>
        <div><Label>Max GPS inaccuracy (m)</Label><Input type="number" value={s.max_accuracy_m} onChange={set("max_accuracy_m", true)} /></div>
      </div>
      <p className="text-xs text-slate-500">
        Clock-out closes at <b>office end + grace</b>; a clock-in without a clock-out by then is <b>Absent — no clock-out</b>.
        An office employee's GPS fix must be at least this accurate to count as inside the radius.{" "}
        <a className="text-blue-700 hover:underline" href={map({ lat: s.office_lat, lng: s.office_lng })} target="_blank" rel="noopener noreferrer">Check the point on a map</a>
      </p>
      <Button onClick={save}><Save className="w-4 h-4 mr-1.5" />Save</Button>
    </div>
  );
}
